import { Button } from "@msuite/ui/button";
import { Textarea } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { getCanApproveAttendance, getIsAttendanceEditable } from "@msuite/utils/attendances";
import { patchAttendanceStateBodySchema } from "@msuite/utils/schemas";
import type { AttendanceListItem, OrganizationAttendanceListItem } from "@msuite/utils/types";
import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { client } from "@/hono";
import { useAttendanceEditorRole } from "@/hooks/use-attendance-editor-role";
import { useCitadel } from "@/hooks/use-citadel";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { EditAttendanceModal } from "@/modals/users/edit-attendance-modal";

export function RowActions({ cell }: CellContext<OrganizationAttendanceListItem, "actions">) {
	/** Context */
	const [loadingState, setLoadingState] = useState<"approve" | "reject" | "reset" | null>(null);
	const { refetch, table } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const asyncModal = useAsyncModal();
	const rejectForm = useForm({
		resolver: resolver(patchAttendanceStateBodySchema),
		defaultValues: {
			state: "rejected",
		},
	});
	const mentionOptions = useMentionOptions();
	const { citadel } = useCitadel();
	const editorRole = useAttendanceEditorRole();

	if (cell.row.original.type === "break") return <div />;

	/** Functions */
	async function handleOnApprove() {
		try {
			setLoadingState("approve");
			const selectedRows = table?.getSelectedRowModel().rows ?? [];
			const rowsToApprove = selectedRows.length > 0 ? selectedRows : [cell.row];

			for (const row of rowsToApprove) {
				const original = row.original as AttendanceListItem;
				await client.users[":userId"].attendances[":attendanceId"].state.$patch({
					param: { userId: original.userId, attendanceId: original.id },
					json: { state: "approved" },
				});
			}

			await refetch?.();
			table.resetRowSelection();
		} catch (_error) {
		} finally {
			setLoadingState(null);
		}
	}

	async function handleOnReject() {
		try {
			setLoadingState("reject");
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			const rejectedReason = rejectForm.getValues("rejectedReason");
			const selectedRows = table?.getSelectedRowModel().rows ?? [];
			const rowsToReject = selectedRows.length > 0 ? selectedRows : [cell.row];

			for (const row of rowsToReject) {
				const original = row.original as AttendanceListItem;
				await client.users[":userId"].attendances[":attendanceId"].state.$patch({
					param: { userId: original.userId, attendanceId: original.id },
					json: { state: "rejected", rejectedReason },
				});
			}

			rejectForm.reset();
			await refetch?.();
			table.resetRowSelection();
		} catch (_error) {
		} finally {
			setLoadingState(null);
		}
	}

	async function handleOnResetState() {
		try {
			setLoadingState("reset");
			const response = await client.users[":userId"].attendances[":attendanceId"].state.$patch({
				param: { userId: cell.row.original.userId, attendanceId: cell.row.original.id },
				json: { state: "pending" },
			});
			await responseHandler(response);
			await refetch?.();
		} catch (_error) {
		} finally {
			setLoadingState(null);
		}
	}

	/** Computed */
	// Approve, reject and reset all write the row's state through the same
	// route, so one right decides whether the verdict controls exist at all.
	const canApproveAttendance = citadel?.can("approve", "Attendance") === true;
	// Correcting a colleague's booked times is a separate right from signing
	// them off — an approver need not be allowed to rewrite the entry. The rule
	// is bound to the row, because everyone holds it for their own entries and
	// nobody for a colleague's; and it lasts while the entry is pending, which
	// is the same state rule the server answers before it writes.
	const canUpdateAttendance =
		citadel?.can("update", "Attendance", {
			orgId: citadel.orgId,
			userId: cell.row.original.userId,
		}) === true && getIsAttendanceEditable(cell.row.original, editorRole);

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				{canApproveAttendance &&
					(cell.row.original.state === "pending" ? (
						<Fragment>
							<Button
								variant="success-ghost"
								icon={<Icon name="Check" />}
								isLoading={loadingState === "approve"}
								isDisabled={!!loadingState || !getCanApproveAttendance(cell.row.original)}
								onClick={handleOnApprove}
							>
								{t("general.approve")}
							</Button>
							<Button
								variant="danger-ghost"
								icon={<Icon name="X" />}
								isLoading={loadingState === "reject"}
								isDisabled={!!loadingState || !getCanApproveAttendance(cell.row.original)}
								onClick={handleOnReject}
							>
								{t("general.reject")}
							</Button>
						</Fragment>
					) : (
						<Button
							icon={<Icon name="RefreshCcw" />}
							isLoading={loadingState === "reset"}
							isDisabled={!!loadingState}
							onClick={handleOnResetState}
						>
							{t("general.reset")}
						</Button>
					))}
				{canUpdateAttendance && (
					<Button
						variant="ghost"
						icon={<Icon name="Edit" />}
						onClick={disclosure.onOpen}
					>
						{t("general.edit")}
					</Button>
				)}
			</RowActionsWrapper>
			{disclosure.isOpen && (
				<EditAttendanceModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					userId={cell.row.original.userId}
					attendanceId={cell.row.original.id}
					onComplete={refetch}
				/>
			)}
			<ConfirmModal
				controller={asyncModal}
				message={null}
				confirmButtonProps={{
					isDisabled: !rejectForm.formState.isValid,
				}}
			>
				<Textarea
					autoFocus
					label={t("users.reject_attendance_reason")}
					isRequired
					control={rejectForm.control}
					name="rejectedReason"
					disableFormatting
					{...mentionOptions}
				/>
			</ConfirmModal>
		</Fragment>
	);
}
